import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const isPendingError = (err) =>
  err?.response?.status === 403 && err?.response?.data?.detail === "device_pending_approval";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [pending, setPending] = useState(false); // device awaiting approval
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setPending(false);
    } catch (err) {
      setUser(false);
      setPending(isPendingError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, code) => {
    const { data } = await api.post("/auth/login", { email, password, code });
    if (data.mfa_required) return data;
    // Confirm device status via /me — a held device gets 403 device_pending_approval.
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      setPending(false);
      return me.data;
    } catch (err) {
      if (isPendingError(err)) {
        setUser(false);
        setPending(true);
        return { pending: true };
      }
      throw err;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setPending(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, pending, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
